import { CrownOutlined, DownloadOutlined, SafetyOutlined } from '@ant-design/icons';
import { StoreDoc, UserDoc, UserStatus } from '@gooduncles/gu-app-schema';
import { NewValueParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { ColDef, ColGroupDef } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Button, Segmented, message, notification } from 'antd';
import confirm from 'antd/es/modal/confirm';
import { BaseOptionType } from 'antd/es/select';
import { get } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { FC } from 'react';
import {
  ServiceVersionFilter,
  getServiceVersionLabel,
  matchesServiceVersionFilter,
  serviceVersionFilterOptions,
  storeStateList,
  userRoleList,
  userStatusKr,
} from 'src/constants';
import { useAuth } from 'src/stores/auth-context';

import { formatDateForAgCell, loadGridState, onValueSetterWithValidation } from 'src/utils/ag-grid-util';
import { AuditLogger } from 'src/utils/audit-logger';
import { getDataDogLinkForUser, parseIfObject, textSort } from 'src/utils/common';
import { callSetUserStatus, callSyncStoreAlgoliaIndex } from 'src/utils/firebase/firebase-callable';
import { FirebaseManager } from 'src/utils/firebase/firebase-manager';

import DownloadRawDataCell from 'src/atomic-components/atoms/AgCell/AgCellDownloadRawData';
import { AgCellStoreState } from 'src/atomic-components/atoms/AgCell/AgCellStoreState';
import ServiceVersionTag from 'src/atomic-components/atoms/ServiceVersionTag';
import AgGridSettingsButtons from 'src/atomic-components/molecules/AgGridSettingsButtons/AgGridSettingsButtons';
import SelectableCell from 'src/components/Common/SelectableCell/SelectableCell';

import classes from './UserTable.module.scss';

import AddressInputButton from './AddressInputButton/AddressInputButton';
import ChangePasswordButton from './ChangePasswordButton/ChangePasswordButton';
import DeadlineNotificationToggleCell from './DeadlineNotificationToggleCell/DeadlineNotificationToggleCell';
import DeviceInfoCell from './DeviceInfoCell/DeviceInfoCell';
import DisableNotificationAtDawnToggleCell from './DisableNotificationAtDawnToggleCell/DisableNotificationAtDawnToggleCell';
import FavoriteProductsEditCell from './FavoriteProductsEditCell/FavoriteProductsEditCell';
import KakaoNotificatoinToggleCell from './KakaoNotificatoinToggleCell/KakaoNotificatoinToggleCell';
import LinkPhoneButton from './LinkPhoneButton/LinkPhoneButton';

const firebaseManager = FirebaseManager.getInstance();
const auditLogger = AuditLogger.getInstance();
const logName = '사용자 관리';
const TABLE_ID = 'userTable';

const defaultColDef: ColDef<UserWithStore> = {
  sortable: true,
  resizable: true,
  filter: true,
};

export type UserWithStore = UserDoc & {
  key: string;
  store?: StoreDoc;
};

const UserRoleCellRenderer = (props: any) => {
  const role: UserWithStore['role'] = props.value;
  if (!role) return null;
  return (
    <span className={role}>
      {role === 'admin' ? <CrownOutlined /> : role === 'manager' ? <SafetyOutlined /> : ''}
      {role}
    </span>
  );
};

const ServiceVersionCellRenderer = (params: any) => <ServiceVersionTag serviceVersion={params.value} />;

/**
 * 관리자 연락처는 store 문서가 아닌 user 문서에 있어 onWriteStoreV2 트리거가 잡지 못한다.
 * 연락처 수정 후 검색 인덱스를 명시적으로 갱신한다.
 * 실패해도 인덱스가 잠깐 낡을 뿐이므로 알리기만 하고 수정을 되돌리지 않는다.
 */
const syncStoreAlgoliaIndex = async (storeId?: string) => {
  if (!storeId) return;
  try {
    const res = await callSyncStoreAlgoliaIndex({ storeId });
    if (res.data.result === 'error') {
      message.warning(`검색 인덱스 동기화 실패: ${res.data.reason ?? '알 수 없는 이유'}`);
    }
  } catch (error: any) {
    console.error(error);
    message.warning(`검색 인덱스 동기화 실패: ${error?.message ?? '알 수 없는 에러'}`);
  }
};

/**
 * 로그등 커스텀 요소 때문에 ag-grid-util에 있는 것을 사용하지 않았다.
 * @param paths field path가 직접적인 경로가 아닌 경우 따로 입력한다.
 */
const onCellValueChangedWithUpdate = async (
  params: NewValueParams<UserWithStore>,
  collection: string,
  docId?: string,
  paths?: string,
  options?: { nullable?: boolean },
) => {
  const { nullable = false } = options ?? {};
  const field = params.colDef.field;
  if (!field) {
    message.error('수정할 수 없는 필드정보입니다.');
    return;
  }

  if (!docId) {
    message.error('id값이 없습니다.');
    return;
  }

  const newValue = get(params.data, field);
  if ((newValue === undefined || newValue === null) && !nullable) {
    message.error('빈 값을 허용하지 않습니다.');
    return;
  }

  if (newValue !== params.oldValue) {
    try {
      if (Array.isArray(newValue)) {
        const uniqueArray = [...new Set(newValue)];
        await firebaseManager.updateDoc(
          collection,
          docId,
          { [paths ?? field]: uniqueArray },
          { audit: { suppress: true } },
        );
      } else {
        await firebaseManager.updateDoc(
          collection,
          docId,
          { [paths ?? field]: newValue },
          { audit: { suppress: true } },
        );
      }
      auditLogger.log({
        action: 'UPDATE',
        entity: collection === 'store' ? 'store' : 'user',
        entityId: docId,
        summary: `${logName} - ${field} 변경`,
        changes: [{ field: paths ?? field, before: params.oldValue, after: newValue }],
      });
      message.success('변경완료');

      // 신규등록 대응 여부를 확인 하기위해 최초 등록하는 경우 알린다.
      if (!params.oldValue && field === 'store.storeCode') {
        const email = params.context.user.email;
        auditLogger.log({
          action: 'UPDATE',
          entity: 'store',
          entityId: docId,
          summary: `${logName} - 매장코드 최초 등록\n매장명: ${params.data.store?.storeName}, 매장코드: ${newValue}\n작업자: ${email}`,
          slack: { channel: '4-앱-알림' },
        });
      }
    } catch (error: any) {
      console.error(error);
      message.error(error?.message ?? '알 수 없는 에러 발생!');
      auditLogger.log({
        action: 'UPDATE',
        entity: collection === 'store' ? 'store' : 'user',
        entityId: docId,
        summary: `${logName} - ${collection}/${docId}.${field}:: ${parseIfObject(error)}`,
        level: 'error',
      });
    }
  }
};

const updateUserStatus = async (userId: string, status: UserStatus) => {
  const response = await callSetUserStatus({ userId, status });
  if (response.data.result === 'success') {
    notification.success({
      message: '사용자 상태 변경 완료',
    });
  } else {
    notification.error({
      message: '사용자 상태 변경 실패',
      description: '관리자에게 문의해주세요.',
    });
  }
  auditLogger.log({
    action: 'STATUS_CHANGE',
    entity: 'user',
    entityId: userId,
    summary: `사용자 상태 변경${response.data.result === 'success' ? '' : ' 실패'} → ${status}`,
    changes: [{ field: 'status', before: undefined, after: status }],
    level: response.data.result === 'success' ? undefined : 'error',
  });
};

const onUserStatusCellChange = async (userId: string, status: UserStatus) => {
  switch (status) {
    case 'active':
      await updateUserStatus(userId, status);
      break;
    case 'inactive':
      confirm({
        title: '사용자 상태를 변경하시겠습니까?',
        content: '정지 상태로 변경하면 앱에서 로그인이 불가능합니다.',
        onOk: () => updateUserStatus(userId, status),
      });
      break;
    default:
      notification.info({
        message: '사용자 삭제는 개발자에게 요청해주세요.',
        description: '사용자 상태를 변경할 수 없습니다.',
      });
      auditLogger.log({
        action: 'STATUS_CHANGE',
        entity: 'user',
        entityId: userId,
        summary: `${logName} - 사용자 삭제 요청`,
        level: 'error',
      });
      break;
  }
};

const UserStatusCell: FC = (params: any) => {
  const { value } = params;
  if (!value) return null;
  const user = params.data as UserWithStore;
  const options: BaseOptionType[] = Object.entries(userStatusKr).map(([value, label]) => ({ value, label }));
  return (
    <SelectableCell
      options={options}
      value={value}
      onChange={(newValue: UserStatus) => onUserStatusCellChange(user._id, newValue)}
      minWidth={100}
    />
  );
};

const autoGroupColumnDef = {
  minWidth: 200,
};

const UserTable: FC<{ rowData: UserWithStore[] }> = ({ rowData }) => {
  const { user } = useAuth();
  const [gridRef, setGridRef] = useState<AgGridReact<UserWithStore> | null>(null);
  const [serviceVersionFilter, setServiceVersionFilter] = useState<ServiceVersionFilter>('all');

  const filteredRowData = useMemo(
    () => rowData.filter((data) => matchesServiceVersionFilter(data.serviceVersion, serviceVersionFilter)),
    [rowData, serviceVersionFilter],
  );

  const storeCodeList =
    filteredRowData.length > 0
      ? (filteredRowData.map((d) => d.store?.storeCode).filter((code) => code) as string[]).sort(textSort)
      : [];

  const columnDefs: (ColDef<UserWithStore> | ColGroupDef<UserWithStore>)[] = useMemo(
    () => [
      { headerName: 'UserId', field: '_id', width: 300, hide: true },
      { headerName: 'StoreId', field: 'store._id', width: 240, hide: true },
      { headerName: 'DeviceId', field: 'deviceTokenId', width: 240, hide: true },
      {
        headerName: 'DeviceInfo',
        field: 'deviceTokenId',
        width: 240,
        hide: true,
        cellRenderer: (params: any) => {
          return <DeviceInfoCell deviceTokenId={params.value ?? null} />;
        },
      },
      {
        headerName: '앱',
        field: 'deviceTokenId',
        enableRowGroup: true,
        width: 72,
        valueFormatter: (params) => {
          const token = params.value;
          return token ? '🟢' : '';
        },
      },
      {
        headerName: '앱 버전',
        field: 'serviceVersion',
        enableRowGroup: true,
        width: 100,
        valueFormatter: (params) => getServiceVersionLabel(params.value),
        cellRenderer: ServiceVersionCellRenderer,
      },
      {
        headerName: 'browser',
        field: 'userAgent.browser.name',
        width: 120,
        hide: true,
      },
      {
        headerName: '빌드',
        field: 'version',
        width: 140,
        headerTooltip: '앱 빌드 버전(타임스탬프)',
      },
      {
        headerName: 'os',
        field: 'userAgent.os.name',
        width: 120,
      },
      {
        headerName: '*권한',
        field: 'role',
        enableRowGroup: true,
        width: 116,
        editable: true,
        cellRenderer: UserRoleCellRenderer,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorPopup: true,
        cellEditorParams: {
          values: userRoleList,
          cellRenderer: UserRoleCellRenderer,
        },
        valueSetter: (params) =>
          onValueSetterWithValidation(params, ['role', '_id'], (value: any) => {
            // value가 admin이면 admin만 가능
            if (params.context.user?.role) {
              if (value === 'admin' && params.context.user?.role === 'admin') {
                return true;
                // 다른 값은 manager, admin면 변경 가능
              } else if (['admin', 'manager'].includes(params.context.user.role)) {
                return true;
              }
            }
            return false;
          }),
        onCellValueChanged: async (params) => {
          const role = params.data.role;
          try {
            await firebaseManager.updateDoc(
              'user',
              params.data._id,
              {
                role,
              },
              { audit: { suppress: true } },
            );
            auditLogger.log({
              action: 'UPDATE',
              entity: 'user',
              entityId: params.data._id,
              summary: `사용자 역할 변경 - ${params.data.email}`,
              changes: [{ field: 'role', before: params.oldValue, after: role }],
            });
            message.success('변경완료');
          } catch (error: any) {
            message.error(error?.message ?? '알 수 없는 에러 발생!');
            auditLogger.log({
              action: 'UPDATE',
              entity: 'user',
              entityId: params.data._id,
              summary: `${logName} - user/${params.data._id}.role:: ${parseIfObject(error)}`,
              level: 'error',
            });
          }
        },
      },
      {
        headerName: '생성일',
        field: '_timeCreate',
        width: 200,
        valueFormatter: formatDateForAgCell('yyyy-MM-dd HH:mm:ss'),
      },
      {
        headerName: '업데이트일',
        field: '_timeUpdate',
        width: 200,
        valueFormatter: formatDateForAgCell('yyyy-MM-dd HH:mm:ss'),
        hide: true,
      },
      {
        headerName: '이메일',
        field: 'email',
        width: 240,
      },
      {
        headerName: '*전화번호',
        field: 'userTel',
        width: 180,
        editable: true,
        cellEditor: 'agTextCellEditor',
        valueSetter: (params) => onValueSetterWithValidation(params, ['_id']),
        onCellValueChanged: async (params) => {
          await onCellValueChangedWithUpdate(params, 'user', params.data._id, 'userTel');
          await syncStoreAlgoliaIndex(params.data.storeId);
        },
      },
      {
        headerName: '*매장상태',
        field: 'store.state',
        enableRowGroup: true,
        width: 120,
        editable: true,
        cellRenderer: AgCellStoreState,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorPopup: true,
        cellEditorParams: {
          values: Object.keys(storeStateList),
          cellRenderer: AgCellStoreState,
        },
        valueSetter: (params) =>
          onValueSetterWithValidation(params, ['store.state'], (value) => {
            if (value === 'open' && !params.data.store?.storeCode) {
              notification.error({
                message: '매장코드가 없습니다.',
                description: '작업이 어려운 경우 슬랙으로 요청해주세요.',
              });
              const email = params.context.user.email;
              auditLogger.log({
                action: 'STATUS_CHANGE',
                entity: 'store',
                entityId: params.data.store?._id ?? '',
                summary: `${logName} - 코드가 없는 매장을 '운영'상태로 변경하려는 시도가 감지되었습니다.\n매장명: ${params.data.store?.storeNickname}\n요청자: ${email}`,
                level: 'error',
                slack: { channel: '4-앱-알림' },
              });
              return false;
            }
            return true;
          }),
        onCellValueChanged: (params) => onCellValueChangedWithUpdate(params, 'store', params.data.store?._id, 'state'),
      },
      {
        headerName: '*매장명',
        field: 'store.storeName',
        width: 180,
        editable: true,
        cellEditor: 'agTextCellEditor',
        valueSetter: (params) => onValueSetterWithValidation(params, ['store.storeName']),
        onCellValueChanged: (params) =>
          onCellValueChangedWithUpdate(params, 'store', params.data.store?._id, 'storeName'),
      },
      {
        headerName: '*매장명(관리)',
        field: 'store.storeNickname',
        width: 180,
        editable: true,
        cellStyle: { color: '#2C2C2C', fontWeight: 600 },
        cellEditor: 'agTextCellEditor',
        valueSetter: (params) => onValueSetterWithValidation(params, ['store.storeNickname']),
        onCellValueChanged: (params) =>
          onCellValueChangedWithUpdate(params, 'store', params.data.store?._id, 'storeNickname'),
      },
      {
        headerName: '매장코드',
        field: 'store.storeCode',
        width: 120,
      },
      {
        headerName: '주소',
        width: 260,
        field: 'store.address',
      },
      {
        hide: true,
        headerName: '시도',
        width: 80,
        field: 'store.sido',
      },
      {
        headerName: '시군구',
        width: 96,
        field: 'store.sigungu',
      },
      {
        headerName: '*상세주소',
        width: 200,
        field: 'store.addressDetail',
        editable: true,
        cellEditor: 'agTextCellEditor',
        valueSetter: (params) => onValueSetterWithValidation(params, ['store._id']),
        onCellValueChanged: (params) =>
          onCellValueChangedWithUpdate(params, 'store', params.data.store?._id, 'addressDetail'),
      },
      {
        headerName: '*즐겨찾기',
        headerTooltip: '개수',
        field: 'favoriteProducts',
        width: 160,
        editable: false,
        cellRenderer: (params: any) => {
          const data = params.data as UserWithStore;
          if (!data?._id) return null;
          return (
            <FavoriteProductsEditCell
              userId={data._id}
              userLabel={data.email ?? data.userName ?? data._id}
              currentIds={data.favoriteProducts ?? []}
              onSaved={(ids) => params.node.setDataValue('favoriteProducts', ids)}
            />
          );
        },
      },
      {
        headerName: '*맞춤상품',
        headerTooltip: '개수',
        field: 'store.unhiddenProducts',
        width: 108,
        editable: true,
        valueSetter: (params) =>
          onValueSetterWithValidation(params, ['store._id'], undefined, {
            type: 'array',
          }),
        onCellValueChanged: (params) => {
          onCellValueChangedWithUpdate(params, 'store', params.data.store?._id, 'unhiddenProducts');
        },
      },
      {
        headerName: '주소입력',
        colId: 'action02',
        width: 160,
        cellRenderer: (params: any) => {
          const data = params.data as UserWithStore;
          const storeId = data?.store?._id;
          if (!storeId) {
            return '';
          }
          return <AddressInputButton storeId={storeId} />;
        },
      },
      {
        headerName: '카톡알림',
        field: 'kakaoNotification',
        width: 160,
        cellRenderer: (params: any) => {
          const data = params.data as UserWithStore;
          if (!data?._id) {
            return null;
          }
          const value = params.value ?? false;
          return <KakaoNotificatoinToggleCell id={data._id} value={value} />;
        },
      },
      {
        headerName: '마감알림',
        field: 'deadlineNotification',
        width: 160,
        cellRenderer: (params: any) => {
          const data = params.data as UserWithStore;
          if (!data?._id) {
            return null;
          }
          const value = params.value ?? false;
          return <DeadlineNotificationToggleCell id={data._id} value={value} />;
        },
      },
      {
        headerName: '새벽 알림 금지',
        field: 'disableNotificationAtDawn',
        width: 160,
        cellRenderer: (params: any) => {
          const data = params.data as UserWithStore;
          if (!data?._id) {
            return null;
          }
          const value = params.value ?? false;
          return <DisableNotificationAtDawnToggleCell id={data._id} value={value} />;
        },
      },
      {
        headerName: '*계정상태',
        field: 'status',
        width: 136,
        cellRenderer: UserStatusCell,
      },
      {
        headerName: '액션',
        width: 160,
        hide: true,
        cellRenderer: DownloadRawDataCell,
      },
      {
        headerName: 'DataDog',
        colId: 'action03',
        width: 140,
        cellRenderer: (params: any) => {
          const data = params?.data as UserWithStore;
          const email = data?.email;
          if (!email) {
            return null;
          }
          return (
            <Button type='link' onClick={() => getDataDogLinkForUser(email, data.serviceVersion)}>
              Link
            </Button>
          );
        },
      },
      {
        headerName: '전화번호 연동 관리',
        colId: 'action04',
        width: 180,
        cellRenderer: (params: any) => {
          const data = params?.data as UserWithStore;
          if (!data?._id) {
            return null;
          }
          return (
            <LinkPhoneButton
              userId={data._id}
              defaultPhone={data.userTel ?? undefined}
              userLabel={data.email ?? data.userName ?? data._id}
              phoneLinked={data.phoneLinked}
            />
          );
        },
      },
      {
        headerName: '비밀번호 변경',
        colId: 'action05',
        width: 160,
        cellRenderer: (params: any) => {
          const data = params?.data as UserWithStore;
          if (!data?._id) {
            return null;
          }
          return (
            <ChangePasswordButton
              userId={data._id}
              userLabel={data.email ?? data.userName ?? data._id}
              targetRole={data.role ?? null}
              storeName={data.store?.storeNickname ?? data.store?.storeName ?? null}
              storeCode={data.store?.storeCode ?? null}
            />
          );
        },
      },
    ],
    [],
  );

  const excelExport = useCallback(() => {
    const columns = gridRef?.columnApi.getColumns();
    if (!gridRef || !columns) {
      notification.error({ message: '파일이 준비되지 않았습니다.' });
      auditLogger.log({
        action: 'SYNC',
        entity: 'user',
        entityId: '',
        summary: `${logName} - Excel export 실패`,
        level: 'error',
      });
      return;
    }

    const columnsToExport = columns
      .map((column) => column.getColId())
      .filter((colId) => !['action01', 'action02', 'action04', 'action05'].includes(colId));
    gridRef?.api.exportDataAsExcel({
      author: 'gooduncles',
      sheetName: '사용자 관리',
      columnKeys: columnsToExport,
    });
    auditLogger.log({
      action: 'SYNC',
      entity: 'user',
      entityId: '',
      summary: `${logName} - Excel export`,
    });
  }, [gridRef]);

  return (
    <div className='ag-theme-alpine height100'>
      <div className={classes.tableButtons}>
        <Segmented
          value={serviceVersionFilter}
          onChange={(value) => setServiceVersionFilter(value as ServiceVersionFilter)}
          options={serviceVersionFilterOptions}
        />
        <AgGridSettingsButtons gridRef={gridRef} tableId={TABLE_ID} />
        <Button type='primary' onClick={excelExport} icon={<DownloadOutlined />}>
          Excel
        </Button>
      </div>
      <AgGridReact
        className={classes.tableContainer}
        ref={setGridRef}
        onGridReady={() => loadGridState(gridRef, TABLE_ID)}
        rowData={filteredRowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        // rowId를 지정해주지 않으면 데이터 변경시 refresh가 발생한다.
        getRowId={(params) => params.data._id}
        context={{ user, storeCodeList }}
        // 편집 완료후 스크롤 이동을 막는다.
        suppressScrollOnNewData={true}
        alwaysShowHorizontalScroll={true}
        animateRows={true}
        autoGroupColumnDef={autoGroupColumnDef}
        rowGroupPanelShow={'always'}
      />
    </div>
  );
};

export default UserTable;
